<template>
  <div
    v-if="selectedLegend"
    class="w-40 bg-white p-base shadow rounded-md"
  >
    <p class="font-medium text-xs text-default-900 pb-base">
      {{ selectedLegend.title }} <span class="sr-only">legend</span>
    </p>
    <ul class="space-y-sm">
      <li
        v-for="value in selectedLegend.values"
        :key="value.label"
        class="flex space-x-xs items-center"
      >
        <span
          class="flex-none block h-4 w-8 rounded-md"
          :style="{ backgroundColor: value.color }"
          aria-hidden="true"
        />
        <span class="text-xs text-default-700">
          {{ value.label }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

const props = defineProps<{
  selectedMapLayer?: string;
}>();

const legends = {
  'air-quality': {
    title: 'Air quality',
    values: [
      {
        color: '#fff',
        label: '0',
      },
      {
        color: '#99ff99',
        label: '1',
      },
      {
        color: '#30ff00',
        label: '2',
      },
      {
        color: '#31cf00',
        label: '3',
      },
      {
        color: '#ffff00',
        label: '4',
      },
      {
        color: '#ffcf03',
        label: '5',
      },
      {
        color: '#ff9a01',
        label: '6',
      },
      {
        color: '#ff6464',
        label: '7',
      },
      {
        color: '#ff0000',
        label: '8',
      },
      {
        color: '#990000',
        label: '9',
      },
      {
        color: '#ce31ff',
        label: '10',
      },
    ],
  },
  'radon-gas': {
    title: 'Radon gas',
    values: [
      {
        color: '#762e3f',
        label: '30 - <100%',
      },
      {
        color: '#9a3c52',
        label: '10 - <30%',
      },
      {
        color: '#af6c72',
        label: '5 - <10%',
      },
      {
        color: '#c5a29f',
        label: '3 - <5%',
      },
      {
        color: '#e7bc9f',
        label: '1 - <3%',
      },
      {
        color: '#ffefd9',
        label: '0 - <1%',
      },
    ],
  },
  'rail-noise': {
    title: 'Rail noise',
    values: [
      {
        color: '#66ff66',
        label: '55.0-59.9',
      },
      {
        color: '#c6d767',
        label: '60.0-64.9',
      },
      {
        color: '#ffff67',
        label: '65.0-69.9',
      },
      {
        color: '#ffb466',
        label: '70.0-74.9',
      },
      {
        color: '#ff3333',
        label: '>=75.0',
      },
    ],
  },
  'road-noise': {
    title: 'Road noise',
    values: [
      {
        color: '#91f791',
        label: '55.0-59.9',
      },
      {
        color: '#c2d77b',
        label: '60.0-64.9',
      },
      {
        color: '#e6f074',
        label: '65.0-69.9',
      },
      {
        color: '#f8c187',
        label: '70.0-74.9',
      },
      {
        color: '#f96b68',
        label: '>=75.0',
      },
    ],
  },
  'soil-texture': {
    title: 'Soil texture',
    values: [
      {
        color: '#ffffbd',
        label: 'Lightest soils',
      },
      {
        color: '#ffebbd',
        label: 'Medium and/to light',
      },
      {
        color: '#d6c39b',
        label: 'Medium soils',
      },
      {
        color: '#d6b29c',
        label: 'Medium and/to heavy',
      },
      {
        color: '#d79e9c',
        label: 'Heaviest soils',
      },
      {
        color: '#cecfce',
        label: 'Mixed or organic',
      },
      {
        color: '#bdebff',
        label: 'N/A',
      },
    ],
  },
  'surface-water': {
    title: 'Surface water',
    values: [
      {
        color: '#000c56',
        label: 'Over 1200mm',
      },
      {
        color: '#353c7e',
        label: '900-1200mm',
      },
      {
        color: '#4262a9',
        label: '600-900mm',
      },
      {
        color: '#5b8ec6',
        label: '300-600mm',
      },
      {
        color: '#98c8ff',
        label: '150-300mm',
      },
      {
        color: '#c5e1ff',
        label: 'Below 150mm',
      },
    ],
  },
};

const selectedLegend = computed(() => legends[props.selectedMapLayer as keyof typeof legends]);
</script>
